import { motion } from "framer-motion";
import { TrendingUp, DollarSign, Calendar, Info } from "lucide-react";
import { ProjectionResults } from "../../utils/projectionCalculations";
import { formatCurrency } from "../../utils/formatters";

interface MetricsDisplayProps {
  projections: ProjectionResults;
}

const MetricsDisplay = ({ projections }: MetricsDisplayProps) => {
  const isProfit = projections.monthlyNetProfit >= 0;

  return (
    <div className="space-y-6">
      {/* Primary Metrics */}
      <div className="grid desktop:grid-cols-3 laptop:grid-cols-3 tablet:grid-cols-3 mobile:grid-cols-1 gap-6">
        <MetricCard
          icon={<DollarSign className="w-8 h-8" />}
          label="Monthly Revenue"
          value={formatCurrency(projections.monthlyRevenue)}
          color="text-success"
          size="large"
        />
        <MetricCard
          icon={<TrendingUp className="w-8 h-8" />}
          label="Annual Run Rate"
          value={formatCurrency(projections.annualRunRate)}
          color="text-brand"
          size="large"
        />
        <MetricCard
          icon={<Calendar className="w-8 h-8" />}
          label="Monthly Net Profit"
          value={formatCurrency(projections.monthlyNetProfit)}
          subtitle={`${projections.profitMargin.toFixed(1)}% margin`}
          color={isProfit ? "text-success" : "text-destructive"}
          size="large"
        />
      </div>

      {/* Per-Pod Breakdown */}
      <div className="bg-card rounded-2xl p-6 shadow-lg border border-border">
        <h3 className="font-funnel font-semibold text-xl text-card-foreground mb-4">
          Per-Pod Economics
        </h3>
        <div className="grid desktop:grid-cols-3 laptop:grid-cols-3 tablet:grid-cols-3 mobile:grid-cols-1 gap-4">
          <div className="bg-muted/30 rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-1">Revenue per Pod</p>
            <p className="font-funnel font-bold text-2xl text-card-foreground">
              {formatCurrency(projections.revenuePerPod)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {projections.utilizedHoursPerPod.toFixed(1)} utilized hrs/month
            </p>
          </div>
          <div className="bg-muted/30 rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-1">Profit per Pod</p>
            <p className="font-funnel font-bold text-2xl text-card-foreground">
              {formatCurrency(projections.profitPerPod)}
            </p>
          </div>
          <div className="bg-muted/30 rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-1">Payback Period</p>
            <p className="font-funnel font-bold text-2xl text-card-foreground">
              {projections.paybackPeriod > 0 
                ? projections.paybackPeriod < 12 
                  ? `${projections.paybackPeriod.toFixed(1)} months`
                  : `${(projections.paybackPeriod / 12).toFixed(1)} years`
                : 'N/A'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Based on £1,120 pod cost
            </p>
          </div>
        </div>
      </div>

      {/* Cost Breakdown */}
      <div className="bg-card rounded-2xl p-6 shadow-lg border border-border">
        <h3 className="font-funnel font-semibold text-xl text-card-foreground mb-4 flex items-center gap-2">
          Cost Structure
          <div className="group relative">
            <Info className="w-4 h-4 text-muted-foreground cursor-help" />
            <div className="absolute left-0 top-6 w-72 bg-background text-foreground text-xs p-3 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10 border border-border">
              Breakdown of monthly costs including revenue sharing, operating expenses, and fixed overhead
            </div>
          </div>
        </h3>
        <div className="space-y-3">
          <CostRow
            label="Total Revenue"
            amount={projections.totalRevenue}
            isPositive
          />
          <CostRow
            label="Revenue Share to Hosts (50%)"
            amount={-projections.revenueShareToHosts}
          />
          <CostRow
            label="Operating Costs (per pod)"
            amount={-(projections.totalOperatingCosts - projections.revenueShareToHosts)}
            subtitle="Maintenance, insurance, payment processing"
          />
          <CostRow
            label="Fixed Overhead"
            amount={-projections.fixedOverhead}
          />
          <div className="border-t-2 border-border pt-3 mt-3">
            <CostRow
              label="Net Profit"
              amount={projections.monthlyNetProfit}
              isResult
              isPositive={projections.monthlyNetProfit >= 0}
            />
          </div>
        </div>
      </div>

      {/* Break-even indicator */}
      <div className="bg-brand/10 rounded-2xl p-6 border border-brand/20">
        <div className="flex items-start gap-4">
          <div className="bg-brand text-brand-foreground rounded-full p-3">
            <TrendingUp className="w-6 h-6" />
          </div>
          <div>
            <h4 className="font-funnel font-semibold text-lg text-foreground mb-1">
              Break-Even Point
            </h4>
            <p className="text-muted-foreground">
              You need <span className="font-bold text-brand">{projections.breakEvenPods} pods</span> to cover fixed overhead costs of {formatCurrency(projections.fixedOverhead)}/month at current pricing and utilization.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const MetricCard = ({
  icon,
  label,
  value,
  subtitle,
  color,
  size = "medium"
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  subtitle?: string;
  color: string;
  size?: "medium" | "large";
}) => {
  return (
    <motion.div
      whileHover={{ y: -4 }}
      className="bg-card rounded-2xl p-6 shadow-lg border border-border"
    >
      <div className={`${color} mb-3`}>{icon}</div>
      <p className="text-sm text-muted-foreground mb-2">{label}</p>
      <motion.p
        key={value}
        initial={{ scale: 1.1 }}
        animate={{ scale: 1 }}
        className={`font-funnel font-bold ${color} ${
          size === "large" ? "text-4xl" : "text-3xl"
        }`}
      >
        {value}
      </motion.p>
      {subtitle && (
        <p className="text-xs text-muted-foreground mt-2">{subtitle}</p>
      )}
    </motion.div>
  );
};

const CostRow = ({
  label,
  amount,
  subtitle,
  isPositive,
  isResult,
}: {
  label: string;
  amount: number;
  subtitle?: string;
  isPositive?: boolean;
  isResult?: boolean;
}) => {
  return (
    <div className="flex items-start justify-between">
      <div>
        <p className={`${isResult ? "font-semibold text-card-foreground" : "text-muted-foreground"}`}>
          {label}
        </p>
        {subtitle && (
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        )}
      </div>
      <p
        className={`font-funnel font-semibold ${
          isResult ? "text-xl" : "text-lg"
        } ${
          isPositive
            ? "text-success"
            : amount < 0
            ? "text-muted-foreground"
            : "text-card-foreground"
        }`}
      >
        {amount >= 0 ? "" : "-"}
        {formatCurrency(Math.abs(amount))}
      </p>
    </div>
  );
};

export default MetricsDisplay;
